import React, { useCallback, useEffect, useState } from 'react';
import { fetchPullList, downloadPullListCsv, type PullListItem } from '../api/reports';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

export const ReportsPullListPage: React.FC = () => {
  const [items, setItems] = useState<PullListItem[]>([]);
  const [totalCards, setTotalCards] = useState(0);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [topNInput, setTopNInput] = useState('100');
  const [minPriceInput, setMinPriceInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(() => {
    const params: { top_n?: number; min_price?: number } = {};

    if (topNInput.trim()) {
      const parsed = parseInt(topNInput, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        params.top_n = parsed;
      }
    }

    if (minPriceInput.trim()) {
      const parsed = parseFloat(minPriceInput);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        params.min_price = parsed;
      }
    }

    return params;
  }, [topNInput, minPriceInput]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchPullList(buildParams());
      setItems(data.items);
      setTotalCards(data.total_cards);
      setGeneratedAt(data.generated_at);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pull list report');
      setItems([]);
      setTotalCards(0);
      setGeneratedAt(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadPullListCsv(buildParams());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Reports: Pull List</h1>
          <p style={styles.subtitle}>Most expensive inventory cards with EDH rank tiebreaking, resolved market pricing, and physical location bins</p>
        </div>

        <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
          <label style={styles.filterLabel}>
            Top N
            <input
              type="number"
              min={1}
              value={topNInput}
              onChange={(e) => setTopNInput(e.target.value)}
              style={styles.input}
              placeholder="100"
            />
          </label>

          <label style={styles.filterLabel}>
            Min Price
            <input
              type="number"
              min={0}
              step="0.01"
              value={minPriceInput}
              onChange={(e) => setMinPriceInput(e.target.value)}
              style={styles.input}
              placeholder="e.g. 25"
            />
          </label>

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryButton} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Apply'}
            </button>
            <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isExporting || isLoading}>
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </form>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.summary}>
          Showing {items.length} rows ({totalCards} total)
          {' · '}
          Generated {formatInstant(generatedAt, '-')}
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Card</th>
                <th style={styles.th}>Set</th>
                <th style={styles.th}>Cond</th>
                <th style={styles.th}>Finish</th>
                <th style={styles.th}>Lang</th>
                <th style={styles.th}>Copies</th>
                <th style={styles.th}>Preferred</th>
                <th style={styles.th}>Market</th>
                <th style={styles.th}>Market Source</th>
                <th style={styles.th}>EDH Rank</th>
                <th style={styles.th}>Physical Locations</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={12} style={styles.emptyCell}>No items match the selected filters.</td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={`${item.rank}-${item.name}-${item.set_name}-${item.condition}-${item.printing}-${item.language}`} style={styles.row}>
                  <td style={styles.td}>{item.rank}</td>
                  <td style={styles.tdStrong}>{item.name}</td>
                  <td style={styles.td}>{item.set_name || '-'}</td>
                  <td style={styles.td}>{item.condition || '-'}</td>
                  <td style={styles.td}>{item.printing || (item.is_foil ? 'foil' : 'normal')}</td>
                  <td style={styles.td}>{item.language || '-'}</td>
                  <td style={styles.td}>{item.copy_count}</td>
                  <td style={styles.tdMoney}>{formatCurrency(item.preferred_price)}</td>
                  <td style={styles.td}>{formatCurrency(item.market_price)}</td>
                  <td style={styles.td}>{formatMarketSource(item.market_source)}</td>
                  <td style={styles.td}>{item.edh_rank ?? '-'}</td>
                  <td style={styles.td}>{item.locations?.length ? item.locations.join(', ') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
  );
};

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) {
    return '-';
  }
  return `$${value.toFixed(2)}`;
}

function formatMarketSource(source: string | null): string {
  if (!source) {
    return '-';
  }
  if (source === 'tcg_market_price') {
    return 'TCG Market';
  }
  if (source === 'market_price') {
    return 'Legacy Market';
  }
  return source;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    marginBottom: spacing.md,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '160px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  summary: {
    marginBottom: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '980px',
  },
  headerRow: {
    backgroundColor: colors.gray100,
    borderBottom: `1px solid ${colors.border}`,
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  tdMoney: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.success,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
};
